/**
 * CollectionCartSidebar
 *
 * Persistent cart sidebar for B2B collection pages.
 *
 * Goals:
 *  - Be a 1:1 mirror of the real Shopify cart at all times.
 *  - Render its initial state from Liquid (snippets/collection-cart-sidebar.liquid).
 *  - Watch #minicart-form so any other piece of code that changes the cart
 *    (drawer +/-/X, product page add to cart, etc.) refreshes the sidebar.
 *  - When the user edits qty / removes / clears inside the sidebar itself,
 *    push the change to /cart and refresh BOTH the drawer AND the sidebar.
 *  - Keep header badge + header total + free-shipping bar in sync after
 *    every mutation, regardless of who triggered it.
 *  - Expose a strong global API (window.CollectionCartSidebarAPI) so other
 *    scripts on the page can update the sidebar deterministically without
 *    relying on the MutationObserver.
 */

(function () {
  // -------------------------------------------------------------------------
  // Standalone helpers shared between the custom element and the global API.
  // We keep them at module scope so they can run even if the custom element
  // class itself isn't yet upgraded (e.g. on very early script execution).
  // -------------------------------------------------------------------------

  function sectionsUrlPreservingView(sectionList) {
    var base = window.location.pathname + window.location.search;
    var sep = base.indexOf("?") !== -1 ? "&" : "?";
    return base + sep + "sections=" + sectionList;
  }

  function applyGlobalCartState(cartData) {
    if (!cartData || typeof cartData.item_count === "undefined") return;
    var itemCount = cartData.item_count;

    if (typeof CartUtils !== "undefined") {
      try {
        CartUtils.updateCartCount(itemCount);
        CartUtils.updateHeaderTotalPrice(cartData);
        CartUtils.updateFreeShippingBar(cartData.items_subtotal_price);
        return;
      } catch (e) {}
    }

    document.querySelectorAll(".cart-count").forEach(function (el) {
      if (el.classList.contains("cart-count-drawer")) {
        el.innerHTML = "(" + itemCount + ")";
      } else {
        el.innerHTML = itemCount > 100 ? "~" : itemCount;
      }
    });

    var headerTotal = document.querySelector("header-total-price");
    if (headerTotal && typeof headerTotal.updateTotal === "function") {
      try { headerTotal.updateTotal(cartData); } catch (e) {}
    }
    var freeShip = document.querySelector("free-ship-progress-bar");
    if (freeShip && typeof freeShip.init === "function") {
      try { freeShip.init(cartData.items_subtotal_price); } catch (e) {}
    }
  }

  /**
   * Inject server-rendered HTML into the sidebar DOM directly.
   * Works even if the <collection-cart-sidebar> custom element class is
   * not yet upgraded – we operate purely on the DOM.
   */
  function injectSectionHtmlIntoSidebar(sectionHtml, itemCount) {
    if (!sectionHtml) return false;
    var sidebar = document.getElementById("collection-cart-sidebar");
    if (!sidebar) return false;

    var doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    var source = doc.getElementById("collection-cart-section-inner");
    if (!source) return false;

    var body = sidebar.querySelector("#collection-cart-sidebar-body");
    var existingFooter = sidebar.querySelector("#collection-cart-sidebar-footer");

    var sourceItems = source.querySelector(".ccs-items");
    var sourceFooter = source.querySelector(".ccs-footer");
    var sourceEmpty = source.querySelector(".ccs-empty");

    if (body) {
      body.innerHTML = "";
      if (sourceItems) {
        var wrapper = document.createElement("div");
        wrapper.className = "ccs-items";
        wrapper.innerHTML = sourceItems.innerHTML;
        body.appendChild(wrapper);
      } else if (sourceEmpty) {
        var empty = document.createElement("div");
        empty.className = "ccs-empty";
        empty.innerHTML = sourceEmpty.innerHTML;
        body.appendChild(empty);
      }
    }

    if (existingFooter && sourceFooter) {
      existingFooter.innerHTML = sourceFooter.innerHTML;
    } else if (!existingFooter && sourceFooter) {
      var newFooter = document.createElement("div");
      newFooter.className = "ccs-footer";
      newFooter.id = "collection-cart-sidebar-footer";
      newFooter.innerHTML = sourceFooter.innerHTML;
      sidebar.appendChild(newFooter);
    } else if (existingFooter && !sourceFooter) {
      existingFooter.remove();
    }

    var units = itemCount;
    if (units == null) {
      var unitsEl = source.querySelector(".ccs-counts-units");
      units = unitsEl
        ? parseInt(unitsEl.textContent, 10) || 0
        : source.querySelectorAll(".ccs-item").length;
    }

    var countEl = sidebar.querySelector(".ccs-header__count");
    if (countEl) countEl.textContent = units > 0 ? "(" + units + ")" : "";
    var mobileStrong = sidebar.querySelector(".ccs-mobile-title .cart-count-sidebar");
    if (mobileStrong) mobileStrong.textContent = units != null ? units : 0;

    // Tell the custom element (if it exists) to re-bind its event listeners
    // on the freshly inserted DOM. If it doesn't exist yet, the listeners
    // will be attached when it upgrades.
    if (sidebar.attachListeners) {
      try { sidebar.attachListeners(); } catch (e) {}
    }
    return true;
  }

  /**
   * Last-resort sidebar refresh that fetches the section and injects it
   * even if the custom element JS hasn't booted up.
   */
  async function rawRefreshSidebar(sectionId) {
    sectionId = sectionId || "collection-cart-section";
    try {
      var res = await fetch(sectionsUrlPreservingView(sectionId), {
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) return false;
      var data = await res.json();
      var html = data[sectionId];
      if (!html) return false;

      try {
        var cartRes = await fetch("/cart.json", {
          headers: { Accept: "application/json" },
        });
        if (cartRes.ok) {
          var cartData = await cartRes.json();
          applyGlobalCartState(cartData);
          return injectSectionHtmlIntoSidebar(html, cartData.item_count);
        }
      } catch (e) {}
      return injectSectionHtmlIntoSidebar(html, null);
    } catch (e) {
      return false;
    }
  }

  // Publish global API immediately, before the custom element is even
  // defined, so the bulk-add can call us during page boot.
  window.CollectionCartSidebarAPI = window.CollectionCartSidebarAPI || {};
  window.CollectionCartSidebarAPI.refresh = function () {
    var el = document.getElementById("collection-cart-sidebar");
    if (el && typeof el.forceRefresh === "function") return el.forceRefresh();
    return rawRefreshSidebar();
  };
  window.CollectionCartSidebarAPI.applyCartState = applyGlobalCartState;
  window.CollectionCartSidebarAPI.applySectionHtml = function (html, itemCount) {
    if (injectSectionHtmlIntoSidebar(html, itemCount)) return true;
    return rawRefreshSidebar();
  };
  window.CollectionCartSidebarAPI.rawRefresh = rawRefreshSidebar;
  window.CollectionCartSidebarAPI.sectionsUrl = sectionsUrlPreservingView;

  // -------------------------------------------------------------------------
  // Custom element
  // -------------------------------------------------------------------------

  class CollectionCartSidebar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || "collection-cart-section";
      this._busy = false;
      this._pendingRefresh = false;

      this._observeMinicart();
      this.attachListeners();
      this._initStickyTop();
      this._initMobileToggle();

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        try {
          this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            this._scheduleRefresh();
          });
        } catch (e) {}
      }
    }

    disconnectedCallback() {
      if (typeof this._cartUpdateUnsub === "function") {
        try { this._cartUpdateUnsub(); } catch (e) {}
      }
      try { this._teardownMobileToggle(); } catch (e) {}
      if (this._ccsTopRaf) { try { cancelAnimationFrame(this._ccsTopRaf); } catch (e) {} }
    }

    /* Public API ---------------------------------------------------- */
    forceRefresh() {
      return this._scheduleRefresh();
    }

    /* Sticky top following the visible header ----------------------- */
    _initStickyTop() {
      // Seguimos el borde inferior REAL del header. Es data-sticky="on-scroll-up":
      // se oculta al bajar y reaparece al subir (transform CSS). Medimos
      // #header-sticky (el inner que se mueve) con un rAF continuo —muestrea
      // cada frame, incluida su animación— para que el top y el alto del
      // carrito (calc(100vh - --ccs-top)) reaccionen suave. Sólo escribimos si
      // el valor cambió (sin churn). El scroll-only anterior laggeaba.
      const header =
        document.getElementById("header-sticky") ||
        document.querySelector(".section-header") ||
        document.querySelector("header");
      // El carrito (fixed) no debe taparse con el footer: cuando éste entra en
      // viewport, limitamos su alto para que termine justo donde empieza el
      // footer. Si el footer no se ve, alto = viewport - top.
      const footer =
        document.querySelector(".footer__middle") ||
        document.querySelector("footer") ||
        document.querySelector(".shopify-section-group-footer-group");
      let lastTop = -1;
      let lastH = -1;
      const update = () => {
        let top = 0;
        if (header) top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        if (top !== lastTop) {
          lastTop = top;
          this.style.setProperty("--ccs-top", `${top}px`);
        }
        let avail = window.innerHeight;
        if (footer) {
          const ft = Math.round(footer.getBoundingClientRect().top);
          if (ft < avail) avail = ft;
        }
        const h = Math.max(0, avail - top);
        if (h !== lastH) {
          lastH = h;
          this.style.setProperty("--ccs-height", `${h}px`);
        }
      };
      // Scroll/resize: actualización SÍNCRONA en el mismo frame del scroll
      // (frame-perfect, sin retraso). El handler corre tras actualizarse el
      // scroll y antes del paint, así el carrito reposiciona en ese mismo frame.
      this._ccsUpdate = update;
      window.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      // rAF continuo: cubre la auto-animación del header (ocultar/mostrar) que
      // ocurre cuando NO estás scrolleando (no dispara eventos 'scroll').
      const loop = () => {
        update();
        this._ccsTopRaf = requestAnimationFrame(loop);
      };
      loop();
    }

    /* Detect cart changes from anywhere on the page ----------------- */
    _observeMinicart() {
      const target = document.getElementById("minicart-form");
      if (!target) {
        document.querySelectorAll(".cart-count").forEach((el) => {
          new MutationObserver(() => this._scheduleRefresh()).observe(el, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        });
        return;
      }
      const observer = new MutationObserver(
        this._debounce(() => this._scheduleRefresh(), 120)
      );
      observer.observe(target, { childList: true, subtree: true });
    }

    /* Refresh queue – never drop a refresh, always converge --------- */
    _scheduleRefresh() {
      if (this._busy) {
        this._pendingRefresh = true;
        return;
      }
      this._busy = true;
      this.classList.add("is-loading");
      return this._refresh()
        .catch(() => {})
        .finally(() => {
          this._busy = false;
          this.classList.remove("is-loading");
          if (this._pendingRefresh) {
            this._pendingRefresh = false;
            this._scheduleRefresh();
          }
        });
    }

    async _refresh() {
      try {
        const response = await fetch(sectionsUrlPreservingView(this.sectionId), {
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) return;
        const data = await response.json();
        const sectionHtml = data[this.sectionId];
        if (!sectionHtml) return;

        let itemCount = null;
        try {
          const cartRes = await fetch("/cart.json", {
            headers: { Accept: "application/json" },
          });
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            itemCount = cartData.item_count;
            applyGlobalCartState(cartData);
          }
        } catch (e) {}

        injectSectionHtmlIntoSidebar(sectionHtml, itemCount);
      } catch (e) {}
    }

    /* Bind +/- / remove / clear-cart buttons ------------------------ */
    attachListeners() {
      this.querySelectorAll(".ccs-qty-btn").forEach((btn) => {
        if (btn._ccsbound) return;
        btn._ccsbound = true;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const key = btn.dataset.key;
          const qty = parseInt(btn.dataset.qty, 10);
          this._changeQty(key, Math.max(0, qty));
        });
      });

      this.querySelectorAll(".ccs-item__remove").forEach((btn) => {
        if (btn._ccsbound) return;
        btn._ccsbound = true;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this._changeQty(btn.dataset.key, 0);
        });
      });

      this.querySelectorAll("input.ccs-qty-val").forEach((input) => {
        if (input._ccsbound) return;
        input._ccsbound = true;
        input._ccsinitialValue = input.value;
        input.addEventListener("change", () => {
          const key = input.dataset.key;
          const qty = Math.max(0, parseInt(input.value, 10) || 0);
          if (String(qty) === input._ccsinitialValue) return;
          input._ccsinitialValue = String(qty);
          this._changeQty(key, qty);
        });
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            input.blur();
          }
        });
        input.addEventListener("focus", () => input.select());
      });

      this.querySelectorAll("[data-ccs-clear-cart]").forEach((btn) => {
        if (btn._ccsbound) return;
        btn._ccsbound = true;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this._askConfirmClear();
        });
      });
    }

    /**
     * In-page confirmation modal (no `window.confirm`).
     * Styled to match the sidebar; auto-removed after a choice or after
     * clicking the backdrop / pressing Escape.
     */
    _askConfirmClear() {
      // Avoid stacking two modals
      const existing = document.getElementById("ccs-confirm-modal");
      if (existing) existing.remove();

      const modal = document.createElement("div");
      modal.id = "ccs-confirm-modal";
      modal.className = "ccs-modal";
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      modal.setAttribute("aria-labelledby", "ccs-confirm-title");
      modal.innerHTML =
        '<div class="ccs-modal__backdrop" data-ccs-modal-close></div>' +
        '<div class="ccs-modal__dialog" role="document">' +
          '<button type="button" class="ccs-modal__close" data-ccs-modal-close aria-label="Cerrar">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">' +
              '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>' +
            "</svg>" +
          "</button>" +
          '<h3 id="ccs-confirm-title" class="ccs-modal__title">Vaciar el carrito</h3>' +
          '<p class="ccs-modal__text">¿Seguro que quieres quitar todos los productos del carrito? Esta acción no se puede deshacer.</p>' +
          '<div class="ccs-modal__actions">' +
            '<button type="button" class="ccs-btn ccs-btn--outline" data-ccs-modal-close>Cancelar</button>' +
            '<button type="button" class="ccs-btn ccs-btn--danger" data-ccs-modal-confirm>Sí, vaciar</button>' +
          "</div>" +
        "</div>";

      const cleanup = () => {
        document.removeEventListener("keydown", onKey);
        if (modal.parentNode) modal.parentNode.removeChild(modal);
      };
      const onKey = (ev) => {
        if (ev.key === "Escape") cleanup();
      };

      modal.addEventListener("click", (ev) => {
        const closer = ev.target.closest("[data-ccs-modal-close]");
        const confirmer = ev.target.closest("[data-ccs-modal-confirm]");
        if (closer) {
          ev.preventDefault();
          cleanup();
        } else if (confirmer) {
          ev.preventDefault();
          cleanup();
          this._clearCart();
        }
      });

      document.body.appendChild(modal);
      document.addEventListener("keydown", onKey);
      // Focus the confirm button so Enter works.
      const focusBtn = modal.querySelector("[data-ccs-modal-confirm]");
      if (focusBtn) {
        try { focusBtn.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    async _changeQty(key, quantity) {
      if (this._busy) {
        this._pendingRefresh = true;
        return;
      }
      this._busy = true;
      this.classList.add("is-loading");

      const changeUrl =
        (window.routes && window.routes.cart_change_url) ||
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_change_url) ||
        "/cart/change.js";

      try {
        const response = await fetch(changeUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!response.ok) return;
        const cartData = await response.json();
        applyGlobalCartState(cartData);
        await this._reloadSectionsAndApply(cartData);
        this._publishCartUpdate(cartData);
      } catch (e) {
      } finally {
        this._busy = false;
        this.classList.remove("is-loading");
        if (this._pendingRefresh) {
          this._pendingRefresh = false;
          this._scheduleRefresh();
        }
      }
    }

    async _clearCart() {
      if (this._busy) {
        this._pendingRefresh = true;
        return;
      }
      this._busy = true;
      this.classList.add("is-loading");

      try {
        const clearResp = await fetch("/cart/clear.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
        });
        if (!clearResp.ok) return;
        const cartData = await clearResp.json();
        applyGlobalCartState(cartData);
        await this._reloadSectionsAndApply(cartData);
        this._publishCartUpdate(cartData);
      } catch (e) {
      } finally {
        this._busy = false;
        this.classList.remove("is-loading");
        if (this._pendingRefresh) {
          this._pendingRefresh = false;
          this._scheduleRefresh();
        }
      }
    }

    async _reloadSectionsAndApply(cartData) {
      try {
        const sectResp = await fetch(
          sectionsUrlPreservingView("minicart-form," + this.sectionId)
        );
        if (!sectResp.ok) return;
        const sectData = await sectResp.json();

        if (sectData["minicart-form"]) {
          const minicartEl = document.getElementById("minicart-form");
          if (minicartEl) {
            const minicartDoc = new DOMParser().parseFromString(
              sectData["minicart-form"],
              "text/html"
            );
            const inner = minicartDoc.getElementById("minicart-form");
            if (inner) minicartEl.innerHTML = inner.innerHTML;
          }
          const cartEl = document.querySelector("cart-notification");
          if (cartEl && typeof cartEl.cartAction === "function") {
            try { cartEl.cartAction(); } catch (e) {}
          }
        }

        if (sectData[this.sectionId]) {
          injectSectionHtmlIntoSidebar(
            sectData[this.sectionId],
            cartData ? cartData.item_count : null
          );
        }
      } catch (e) {}
    }

    _publishCartUpdate(cartData) {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "collection-cart-sidebar",
            cartData,
          });
        } catch (e) {}
      }
    }

    /* Mobile: carrito colapsable (bottom-sheet) -------------------- */
    _initMobileToggle() {
      this._mq = window.matchMedia("(max-width: 1024px)");
      // La barra colapsada entera expande; ya expandido, sólo el título
      // (handle) o el backdrop colapsan, para no robar taps a los items.
      if (!this._ccsToggleBound) {
        this._ccsToggleBound = true;
        this.addEventListener("click", (e) => {
          if (!this._mq.matches) return;
          if (this.classList.contains("is-expanded")) {
            if (e.target.closest(".ccs-mobile-title")) this._collapseMobile();
          } else {
            this._expandMobile();
          }
        });
      }
      this._mqHandler = () => { if (!this._mq.matches) this._collapseMobile(); };
      if (this._mq.addEventListener) this._mq.addEventListener("change", this._mqHandler);
      else if (this._mq.addListener) this._mq.addListener(this._mqHandler);
    }

    _expandMobile() {
      this.classList.add("is-expanded");
      if (!this._backdrop) {
        const bd = document.createElement("div");
        bd.className = "ccs-mobile-backdrop";
        bd.addEventListener("click", () => this._collapseMobile());
        document.body.appendChild(bd);
        this._backdrop = bd;
      }
    }

    _collapseMobile() {
      this.classList.remove("is-expanded");
      if (this._backdrop) {
        this._backdrop.remove();
        this._backdrop = null;
      }
    }

    _teardownMobileToggle() {
      if (this._mq && this._mqHandler) {
        if (this._mq.removeEventListener) this._mq.removeEventListener("change", this._mqHandler);
        else if (this._mq.removeListener) this._mq.removeListener(this._mqHandler);
      }
      this._collapseMobile();
    }

    _debounce(fn, ms) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
      };
    }
  }

  if (!customElements.get("collection-cart-sidebar")) {
    customElements.define("collection-cart-sidebar", CollectionCartSidebar);
  }
})();
